import React, { useState, useEffect, useCallback } from "react";
import Select, { components } from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { Button, Modal, Row, Col, Form } from "react-bootstrap";
import Icon from "components/Icons/Icon";

import SaveSiteSuccess from "../../../AddSingleSite/SaveSiteSuccess";

import Alert from "components/Common/Alert";

import './AddSiteAsset.scss';

import { Tooltip } from "components/Common/Tooltip/Tooltip";
import _ from "lodash";
import Loading from "components/Loading";

import { loadCompanyAssetsStart } from "services/redux/actions/serviceDesk";
import { getCompanyIdFromRoute } from 'services/redux/selectors/company';

import { darkModeSelectStyles } from "components/Common/CustomStyles/darkModeSelectStyles";

import conxtdOut from "apis/conxtdOut";

/**
 * Service Desk -> Site Editor -> Asset Manager -> Add Site Asset
 *
 * Allows user to add a single site asset to the selected site
 *
 **/
const AddSiteAsset = ({
  // props
  navName,
  handleClose,
  handleShow,
  localAssetId,
  showModal,
  siteAssetFields,
  sudoSiteId,
  mctClusterId,
  mobileAppId,
  setForceReload,
  additionalSections,
  categoryCustomFields,
  siteSelectionInvalid,
  categorySelectionInvalid,
}) => {

  // REDUX SELECTORS ------
  const { darkMode } = useSelector((state) => state.app)
  const companyId = useSelector(getCompanyIdFromRoute);
  // -------------------

  const dispatch = useDispatch();
  
  // STATE MANAGEMENT ------
  const [defaultFields, setDefaultFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoFileName, setPhotoFileName] = useState(null);
  const [photoFieldId, setPhotoFieldId] = useState();
  
  // form validation states
  const [nameInvalid, setNameInvalid] = useState(true);
  const [photoFileInvalid, setPhotoFileInvalid] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [disabledButton, setDisabledButton] = useState("");
  // ----------------------------------------------------------------

  // checks if additional section components exist (from Company Asset Page) --->
  const [hasAdditionalSections, setHasAdditionalSections] = useState(false);
  
  useEffect(() => {
    setHasAdditionalSections(Array.isArray(additionalSections) && additionalSections.length > 0);
  }, [additionalSections])
  // ----------------------------------------------------------------------------

  // retrieves photo field id from default fields
  const getPhotoFieldId = () => {
    if (defaultFields.length > 0) {
      const photoField = Object.values(defaultFields).filter(value => value?.field_name === 'Photo' && value?.field_uid);
      return photoField && photoField[0]?.field_uid;
    }
    return null;
  }

  useEffect(() => {
    setPhotoFieldId(getPhotoFieldId)
  }, [defaultFields])
  
  // --------------------------------------------

  // form data state
  const [formData, setFormData] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false)
  // --------------------------------------------

  // form validation
  useEffect(() => {
    setInvalid(photoFileInvalid || nameInvalid || siteSelectionInvalid || categorySelectionInvalid);
  }, [setInvalid, photoFileInvalid, nameInvalid, siteSelectionInvalid, categorySelectionInvalid])

  const initialiseState = useCallback(() => {
    setPhotoFile(null);
    setPhotoFileName(null);
    setPhotoFileInvalid(null);
    setNameInvalid(true);
    setFormData([]);
  }, [
    setPhotoFile,
    setPhotoFileName,
    setPhotoFileInvalid,
    setNameInvalid,
    setFormData,
  ])

  const [notNull, setNotNull] = useState(false);

  useEffect(() => {
    const hasNonNullValue = formData.some(
      (item) => item?.value !== null && item?.value !== undefined && item?.value !== ''
    );
    setNotNull(hasNonNullValue);
  }, [formData, setNotNull]);

  useEffect(() => {
    invalid ? setDisabledButton("disabled") : setDisabledButton("");
  }, [invalid, setDisabledButton])

  const checkClose = useCallback(() => {
    if (notNull) {
      Alert({
        text: `Close this window? Any unsaved changes will be lost.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Close",
        cancelButtonText: "Cancel",
      }).then(result => {
        if (result.value) {
          handleClose();
          initialiseState();
        } else {
          return;
        }
      })
    } else {
      handleClose();
      initialiseState();
      return;
    }
  }, [handleClose, initialiseState, notNull])

  const submitForm = () => {
    // converts form data state object into multipart data FormElement
    const multiFormDataElement = new FormData();
    formData.forEach(({ field_uid, value }) => {
      if (value instanceof File) {
        multiFormDataElement.append(field_uid, photoFile)
      } else {
        multiFormDataElement.append(field_uid, value)
      }
    });

    Alert({
      text: `Are you sure you want to add this asset?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Add asset",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value && localAssetId && sudoSiteId) {
        const submit = conxtdOut.post(`/company-assets/add-site/${localAssetId}?sudo_site_id=${Number(sudoSiteId)}&mobile_app_id=${mobileAppId}&mct_cluster_id=${mctClusterId}`, multiFormDataElement)
        setSubmitLoading(true);
        try {
          submit.then(() => {
            handleClose();
            setSubmitLoading(false);
            setShowAlert(true);
            initialiseState();
            if (hasAdditionalSections) {
              dispatch(loadCompanyAssetsStart({ companyId }));
            } else {
              setForceReload(true);
            }
        });
        } catch (error) {
          setSubmitLoading(false);
          console.log("error: ", error)
            Alert({
              text: "Adding asset has failed, please try again.",
              icon: "error",
              timerProgressBar: true,
              timer: 5000,
            });
        }
      } else {
        return
      }
    })
  }

  // Custom Select Component and Options -----
  const CustomOption = (props) => {
    const { data } = props;

    if (!data?.label || !data?.value) { return null };

    return (
      <components.Option {...props}>
        <div className='d-flex align-items-center pl-1 pr-1'>
          <Icon style={{ color: data?.colour ?? 'lightgray' }} className='fas fa-circle fa-xs mr-2' />
          <span>{data.label}</span>
        </div>
      </components.Option>
    );
  };
  const CustomSingleValue = (props) => {
    const { data } = props;

    if (!data?.label || !data?.value) { return null };

    return (
      <components.SingleValue {...props}>
        <div className='d-flex align-items-center pl-1 pr-1'>
          <Icon style={{ color: data?.colour ?? 'lightgray' }} className='fas fa-circle fa-xs mr-2' />
          <span>{data.label}</span>
        </div>
      </components.SingleValue>
    );
  };
  // -----------

  const handleSetState = (setState, fieldId, newVal) => {
    setState((prev) => {
      const prevState = Array.isArray(prev) ? prev : [];

      const existingIndex = prevState.findIndex(
        (item) => item.field_uid === fieldId
      );

      if (existingIndex !== -1) {
        // Update existing item
        const updatedValues = prevState.map((item, index) =>
          index === existingIndex
            ? { ...item, value: newVal }
            : item
        );
        return updatedValues;
      } else {
        // Add new item
        return [...prevState, { field_uid: fieldId, value: newVal }];
      }
    });
  }

  // function that renders asset fields and sets data values to appropriate state for form submission
  const mapFields = (state, setState) => {
    const entries = (Array.isArray(state) ? state : Object.entries(state)).filter(value => value?.field_name !== 'Photo');

    const groupedFields = [];
    for (let i = 0; i < entries.length; i += 2) {
      groupedFields.push(entries.slice(i, i + 2));
    }

    const render = groupedFields.map((group, idx) => (
      <Row key={idx} className="w-100">
        {Object.entries(group).map(([key, value]) => {
          const { field_uid, field_name, type, options } = value;
          
          const handleFormattedOptions = () => {
            if (!options || Array.isArray(options) && options?.length <= 0) { return []};
            if (options === null || options === undefined) { return [] };
            
            const list = Object.values(options);
            if (list === null || list === undefined) { return [] };
            if (type === 'dropdown') {
              return list?.map(o => {
                if (!o?.option) { return null }
                return { 
                  label: o?.option ?? "", 
                  value: o?.option ?? "", 
                  colour: o?.colour ?? "transparent" 
                }
              })
              ?.filter(i => i !== null)
            }
          }

          const formattedOptions = handleFormattedOptions();

          return (
            <Col key={field_uid ?? key} md={6} className="mb-3">
              {
                field_name === "Name" ?
                  <Form.Label>Name *</Form.Label>
                :
                  <Form.Label>{field_name ?? key}</Form.Label>
              }
              {type === 'dropdown' ?
                <Select
                  data-intercom-target={`${field_name ?? 'add-asset-dropdown'}-input-field`}
                  components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
                  options={formattedOptions ?? []}
                  type="text"
                  maxLength={50}
                  name={`${field_name ?? 'add-asset-dropdown'}_input`}
                  placeholder="Select an option..."
                  onChange={(option) => {
                    const newVal = option?.value;
                    const fieldId = field_uid ?? key;
                    const fieldName = field_name;
                    fieldName === "Name" && setNameInvalid(!newVal);
                    handleSetState(setState, fieldId, newVal);
                  }}
                  styles={{
                    ...(darkMode ? darkModeSelectStyles : ""),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    container: (base, { selectProps }) => ({
                      ...base,
                      ...(selectProps?.menuIsOpen ? {
                        position: 'relative',
                        zIndex: 9999,
                      } : {})
                    }),
                  }}
                />
              :
                <Form.Control
                  name={field_name}
                  maxLength={50}
                  onChange={(e) => {
                    const newVal = e.target.value;
                    const fieldId = field_uid ?? key;
                    const fieldName = field_name;
                    fieldName === "Name" && setNameInvalid(!newVal);
                    handleSetState(setState, fieldId, newVal);
                    }}
                  />
                }

              </Col>
            )
          })
        }
      </Row>
    ));
    return render;
  };


  // -------------------

  // USEEFFECT HOOKS --------

  useEffect(() => {
    if (siteAssetFields.length > 0) {
      setDefaultFields(siteAssetFields.filter(field => field.default))
      // sets custom fields based on conditional check whether additional sections exits 
      // and they contain custom fields of their own --------------->
      setCustomFields(hasAdditionalSections ?
        (Array.isArray(categoryCustomFields) && categoryCustomFields.length > 0 ? categoryCustomFields : []) : 
        siteAssetFields.filter(field => field.default === false))
      }
      // ------------------------------------------------------------
  }, [siteAssetFields, setDefaultFields, setCustomFields, additionalSections])

  useEffect(() => {
    !showModal && initialiseState();
  }, [showModal, initialiseState])

  // Photo upload handler ------>
  const onFileChange = (e) => {
    setPhotoFile(e.target.files[0]);
    setPhotoFileName(e.target.files[0].name);
  };

  useEffect(() => {
    if (photoFieldId) {
      setFormData((prev) => {
        const prevState = Array.isArray(prev) ? prev : [];
        const existingIndex = prevState.findIndex(
          (item) => item.field_uid === photoFieldId
        );

        if (existingIndex !== -1) {
          // Update existing item
          const updatedValues = prevState.map((item, index) =>
            index === existingIndex
              ? { ...item, value: photoFile }
              : item
          );
          return updatedValues;
        } else {
          // Add new item
          return [...prevState, { field_uid: photoFieldId, value: photoFile }];
        }
      });

      if (photoFile) {
        if (photoFile?.size > 2 * 1024 * 1024) {
          setPhotoFileInvalid("File is too large")
        } else if (!["image/png", "image/jpeg"].includes(photoFile?.type)) {
          setPhotoFileInvalid(
            <>
              File is not an image format that we accept. Suggested formats are:
              <span className="font-italic font-weight-bold ml-1">jpeg, png</span>
            </>
          )
        } else {
          setPhotoFileInvalid(false);
        }
      }
    }
  }, [photoFile, setFormData])
  // -------------------------

  // VIEW -------
  return (
    <>
      <Modal
        backdrop="static"    
        centered
        keyboard={false}
        onHide={checkClose}
        show={showModal}
        size="xl"
      >
        <Form className="add-asset-form" onSubmit={submitForm}>
          <Modal.Header
            closeButton
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Modal.Title>
              {navName ? <div>Add Asset To <span className="font-weight-bold">{navName}</span></div> : 'Add Asset'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className={`${darkMode ? "darkMode" : "background-modal-wrapper"}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {/* Main container */}
            <div className={`container d-flex justify-content-center ${darkMode ? "darkMode" : "background-modal-wrapper"}`}>
              <Row className="w-100">

                {hasAdditionalSections ?
                  additionalSections.map(({ id, name, component }) => {
                    return (
                      <div key={id} className="custom-card w-100" data-intercom-target={name}>
                        <Row className="w-100">
                          <Col>
                            <Form.Text className="custom-card-header">{name}</Form.Text>
                            <Row className="fields-container p-3">
                              {component}
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    )
                  })
                :
                  null
              }
                
                {/* Default Fields */}
                  <div className="custom-card w-100" data-intercom-target={"add-asset-default-fields"}>
                    <Row className="w-100">
                      <Col>
                        <Form.Text className="custom-card-header">Default Fields</Form.Text>
                        <Row className="fields-container p-3">
                          {defaultFields && mapFields(defaultFields, setFormData)}
                        </Row>
                      </Col>
                    </Row>
                  </div>
                
                {/* Custom Fields */}
                {customFields.length > 0 &&
                  <div className="custom-card w-100" data-intercom-target={"add-asset-custom-fields"}>
                    <Row className="w-100">
                      <Col>
                        <Form.Text className="custom-card-header">Custom Fields</Form.Text>
                        <Row className="fields-container p-3">
                          {mapFields(customFields, setFormData)}
                        </Row>
                      </Col>
                    </Row>
                  </div>
                }

                {/* Image */}
                  <div className="custom-card w-100" data-intercom-target={"add-asset-default-fields"}>
                    <Row className="w-100">
                      <Col>
                        <span className="d-flex align-items-center">
                          <Form.Text className="custom-card-header">Image</Form.Text>
                          <p className="ml-2 font-italic">- files must be smaller than 2mbs</p>
                        </span>
                        <Row className="fields-container p-3">
                          <div className="custom-file mb-2">
                            <input
                              type="file"
                              className="custom-file-input"
                              id="photo-file-input"
                              onChange={onFileChange}
                            />
                            <label className="custom-file-label" htmlFor="customFile">
                              {photoFileName}
                            </label>
                          </div>
                          <Form.Control.Feedback type="warning" className="trigger-feedback-invalid col p-0">
                            <div className={`feedback-container-${photoFileInvalid ? "after" : "before"}`}>
                                <div className="d-flex align-items-center flex-wrap justify-content-between">
                                  <div className="feedback-text-stack col-9">
                                    {photoFileInvalid}
                                  </div>
                                  {/* <Button className="feedback-button col-3" variant="primary" data-intercom-target={"open-chat-button-add-site"}>Open Chat</Button> */}
                                </div>
                            </div>
                          </Form.Control.Feedback>
                        </Row>
                      </Col>
                    </Row>
                  </div>

              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="container">
              <div className="d-flex justify-content-between align-items-center">
                <div>* = required field</div>
                <div>
                  <Button
                    data-intercom-target={"close-modal-button-add-site"}
                    variant="primary"
                    className="mr-2"
                    onClick={() => checkClose()}
                  >
                    Close
                  </Button>
                  {invalid
                    ?
                      <Tooltip
                        placement="top"
                        description={
                          <ul className="m-0 pl-3 text-left">
                            {nameInvalid ? <li key={1}><span className="font-italic">name field is required</span></li> : null}
                            {photoFileInvalid  ? <li key={2}><span className="font-italic">incorrect image format used</span></li> : null}
                            {siteSelectionInvalid  ? <li key={3}><span className="font-italic">site selection is required</span></li> : null}
                            {categorySelectionInvalid  ? <li key={4}><span className="font-italic">category selection is required</span></li> : null}
                          </ul>
                        }
                      >
                        <Button variant="success" className={invalid ? `${disabledButton}` : ""} data-intercom-target={"submit-button-add-site"}>Submit</Button>
                      </Tooltip>
                    :
                      <Button variant="success" onClick={submitForm} data-intercom-target={"submit-button-add-site"}>
                        {submitLoading ? <Loading /> : "Submit"}
                      </Button>
                  }
                </div>
              </div>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      <SaveSiteSuccess
        handleAddShow={handleShow}
        setShowAlert={setShowAlert}
        showAlert={showAlert}
        inputTextMain={'Asset added successfully. Add another asset?'}
        inputTextButton={'Add Asset'}
      />

    </>
  );
  // -------------------
}

export default AddSiteAsset;